/*---------------------------------------------------------------------------------------------
 *  Copyright (c) i-Bridge Systems Philippines Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/
import { BigModal } from './BigModal';
import { ModalButton } from './ModalButton';
import { Typography, Grid, MenuItem } from '@mui/material';
import { CustomTextField, SelectDropdown, SuccessModal } from '../index';
import { apiUrl } from '../../utilities/apiUrl';
import { useState } from 'react';
import { useAlert } from '../../providers/AlertContext';

export const AddEmpModal = (props) => {
  const [lastname, setLastname] = useState('');
  const [firstname, setFirstname] = useState('');
  const [middlename, setMiddlename] = useState('');
  const [sex, setSex] = useState('Male');
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [role, setRole] = useState('REGULAR_USER');
  const [addSuccessOpen, setAddSuccessOpen] = useState(false);
  const [isMatchingPassword, setIsMatchingPassword] = useState(false);
  const { showAlert } = useAlert();

  // ADD EMPLOYEE SUBMIT HANDLER
  const handleSubmit = () => {
    //setIsSending(true);

    fetch(apiUrl('/employee'), {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        username: username,
        middlename: middlename,
        password: password,
        email: email,
        sex: sex,
        firstname: firstname,
        lastname: lastname,
        role: role,
      }),
    })
      .then((response) => response.json())
      .then((data) => {
        // setIsSending(false);
        if (data.success) {
          setAddSuccessOpen(true);
        } else {
          showAlert(data.message, 'warning', 'Invalid Input');
        }
      })
      .catch((err) => {
        console.log(err);
      });
  };

  // SUCCESSFULLY ADDED A NEW EMPLOYEE MODAL
  const handleAddSuccessClose = () => {
    props.onClose();
    setAddSuccessOpen(false);
    // reset values
    setLastname(null);
    setFirstname(null);
    setUsername(null);
    setEmail(null);
    setPassword(null);
    setConfirmPassword(null);
    setRole('REGULAR_USER');
    window.location.reload();
  };

  return (
    <>
      <BigModal open={props.open} onClose={props.onClose}>
        <Typography sx={{ mb: 2, fontWeight: 600 }} variant="h5">
          Add new employee:
        </Typography>
        <Grid container>
          <Grid item xs={12}>
            <CustomTextField
              error={!username}
              errorMsg={'Please enter a valid username'}
              label={'Username'}
              onChange={(event) => {
                setUsername(event.target.value);
              }}
              required={true}
            />
          </Grid>
          <Grid item xs={12}>
            <CustomTextField
              error={!email}
              errorMsg={'Please enter a valid email'}
              label={'Email'}
              onChange={(event) => {
                setEmail(event.target.value);
              }}
              required={true}
            />
          </Grid>
          <Grid item xs={12}>
            <Grid
              container
              spacing={1}
              sx={{ display: 'flex', justifyContent: 'space-between' }}
            >
              <Grid item xs={6}>
                <CustomTextField
                  error={!firstname}
                  errorMsg={'Please enter your first name'}
                  label={'First Name'}
                  onChange={(event) => {
                    setFirstname(event.target.value);
                  }}
                  onFocus={true}
                  required={true}
                />
              </Grid>
              <Grid item xs={6}>
                <CustomTextField
                  error={!lastname}
                  errorMsg={'Please enter your last name'}
                  label={'Last Name'}
                  onChange={(event) => {
                    setLastname(event.target.value);
                  }}
                  required={true}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Grid
              container
              spacing={1}
              sx={{ display: 'flex', justifyContent: 'space-between' }}
            >
              <Grid item xs={6}>
                <CustomTextField
                  fullWidth
                  label="Middle Name"
                  onChange={(event) => setMiddlename(event.target.value)}
                />
              </Grid>

              <Grid item xs={6}>
                <SelectDropdown
                  fullWidth
                  require
                  margin="normal"
                  label="Sex"
                  value={sex}
                  error={!sex}
                  helperText={!sex ? 'Select a sex' : ''}
                  onChange={(event) => setSex(event.target.value)}
                >
                  <MenuItem value="Male">Male</MenuItem>
                  <MenuItem value="Female">Female</MenuItem>
                </SelectDropdown>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <CustomTextField
              error={!password}
              errorMsg={'Please enter a valid password'}
              label={'Password'}
              type={'password'}
              onChange={(event) => {
                setPassword(event.target.value);
                event.target.value === confirmPassword
                  ? setIsMatchingPassword(true)
                  : setIsMatchingPassword(false);
              }}
              required={true}
            />
          </Grid>
          <Grid item xs={12}>
            <CustomTextField
              error={!isMatchingPassword}
              errorMsg={'Your passwords must match'}
              label={'Confirm Password'}
              type={'password'}
              onChange={(event) => {
                setConfirmPassword(event.target.value);
                event.target.value === password && event.target.value !== ''
                  ? setIsMatchingPassword(true)
                  : setIsMatchingPassword(false);
              }}
              required={true}
            />
          </Grid>
          <Grid item xs={12}>
            <SelectDropdown
              fullWidth={true}
              require={true}
              margin="normal"
              label="Role"
              value={role}
              error={!role}
              helperText={!role ? 'Select a role' : ''}
              onChange={(event) => {
                setRole(event.target.value);
              }}
            >
              <MenuItem value={'REGULAR_USER'}>Regular User</MenuItem>
              <MenuItem value={'ADMIN'}>Admin</MenuItem>
            </SelectDropdown>
          </Grid>
        </Grid>
        <ModalButton
          primaryBtnName="Submit"
          secondaryBtnName="Cancel"
          primaryClickHandler={() => {
            handleSubmit();
          }}
          secondaryClickHandler={props.secondaryClick}
          // Ensures all fields are properly filled before submit button becomes available
          disable={
            !firstname ||
            !lastname ||
            !email ||
            !sex ||
            !username ||
            !password ||
            !isMatchingPassword ||
            !role
          }
        />
      </BigModal>
      <SuccessModal
        open={addSuccessOpen}
        onClose={() => {
          setAddSuccessOpen(false);
          handleAddSuccessClose();
        }}
        close={handleAddSuccessClose}
        desc={`You have succesfully added ${firstname} ${lastname} as a new employee.`}
      />
    </>
  );
};
